import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { outcomeKey, type Handover, type PurchaseOutcome } from '@entities/handover';
import { ApiError } from '@shared/api/problem';
import { money } from '@shared/lib/money';
import { Badge, Button } from '@shared/ui';

import styles from './Handover.module.css';

interface PurchaseOutcomeFormProps {
  state: Handover;
  pending: boolean;
  error: unknown;
  onRecord: (value: PurchaseOutcome, confirmed: boolean) => void;
}

/** Порядок вариантов — порядок §39.3: купил, не купил, ещё думаю. */
const OPTIONS: PurchaseOutcome[] = ['purchased', 'not_purchased', 'undecided'];

/**
 * Отметка исхода покупки (FE-260, MVP_PRD §39.3).
 *
 * Пять правил блока:
 *
 * * **«купил» подтверждается вторым шагом** с явным перечнем того, что будет
 *   зафиксировано: финальная цена и полученные сверх неё позиции. Пользователь
 *   видит перечень **до** нажатия и может вернуться к любому полю, поправив его
 *   в переписке, — а не узнаёт о зафиксированном после;
 * * **сумма вознаграждения названа до нажатия, а не после** (§39.3). Её
 *   присылает сервер (`terms.fee_estimate`) той же функцией, что начислит её
 *   при отметке (BE-593, находка H-7: своя формула экрана обещала 50 €, а
 *   начислено было 100 €), и помечена она **предварительной**: окончательный
 *   расчёт с основаниями и цитатами появляется сразу после отметки (§42.4);
 * * **отсутствие отметки — не покупка.** Пока пользователь не выбрал, заказ
 *   остаётся на шаге 7, и никакого «наверное, купил» не появляется;
 * * **«ещё думаю» заказ не закрывает.** Это ожидание, а не результат: `completed`
 *   терминальна, и завершать ею заказ, о котором сказано «я не решил», значило
 *   бы запереть его одним нажатием;
 * * **исход не используется для оптимизации алгоритмов** (§39.3) — и это
 *   сказано на экране: отказ от плохого автомобиля такой же успешный исход,
 *   как покупка, и подбор от него не меняется.
 */
export function PurchaseOutcomeForm({ state, pending, error, onRecord }: PurchaseOutcomeFormProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const [choice, setChoice] = useState<PurchaseOutcome | null>(null);

  const completed = state.status === 'completed';
  const failure = errorText(error, t);
  const terms = state.terms;
  const estimate = terms?.fee_estimate ?? null;

  if (completed) {
    return (
      <section className={styles.section} aria-labelledby="handover-outcome">
        <h2 id="handover-outcome" className={styles.title}>
          {t('handover.outcome.title')}
        </h2>
        <p className={styles.subtitle}>
          <Badge tone="neutral">{t(outcomeKey(state.purchase_outcome ?? 'undecided'))}</Badge>
        </p>
        <p className={styles.note}>
          {t('handover.outcome.recordedAt', {
            date: new Date(state.purchase_outcome_at ?? '').toLocaleString(locale),
          })}
        </p>
        <p className={styles.note}>{t('handover.outcome.notForAlgorithms')}</p>
      </section>
    );
  }

  return (
    <section className={styles.section} aria-labelledby="handover-outcome">
      <h2 id="handover-outcome" className={styles.title}>
        {t('handover.outcome.title')}
      </h2>
      <p className={styles.subtitle}>{t('handover.outcome.subtitle')}</p>

      {state.purchase_outcome === null ? null : (
        <p className={styles.note}>
          {t('handover.outcome.current', {
            value: t(outcomeKey(state.purchase_outcome)),
          })}
        </p>
      )}

      {failure === null ? null : (
        <p className={styles.error} role="alert">
          {failure}
        </p>
      )}

      {choice === null ? (
        <div className={styles.outcomeChoice}>
          {OPTIONS.map((value) => (
            <button
              key={value}
              type="button"
              className={styles.outcomeOption}
              aria-pressed={state.purchase_outcome === value}
              disabled={pending}
              onClick={() => {
                // «Ещё думаю» и «не купил» ничего не начисляют и второго шага
                // не требуют — их backend принимает сразу.
                if (value === 'purchased') {
                  setChoice(value);
                  return;
                }
                onRecord(value, false);
              }}
            >
              <span className={styles.outcomeLabel}>{t(outcomeKey(value))}</span>
              <span className={styles.outcomeHint}>{t(`handover.outcome.hint.${value}`)}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className={styles.confirmBox} role="group" aria-live="polite">
          <p className={styles.confirmTitle}>{t('handover.outcome.confirmTitle')}</p>
          <p className={styles.subtitle}>{t('handover.outcome.confirmText')}</p>

          {/* Явный перечень того, что будет зафиксировано (§39.3). */}
          <dl className={styles.facts}>
            <div>
              <dt>{t('handover.terms.finalPrice')}</dt>
              <dd className="tabular">
                {terms?.price_cents == null
                  ? t('common.noData')
                  : money(terms.price_cents, terms.currency, locale, 'auto')}
              </dd>
            </div>
            <div>
              <dt>{t('handover.outcome.promisedCount')}</dt>
              <dd>{terms?.promised.length ?? 0}</dd>
            </div>
          </dl>

          {terms && terms.promised.length > 0 ? (
            <ul className={styles.quotes}>
              {terms.promised.map((item) => (
                <li key={`${item.label}:${item.quote}`}>
                  <span className={styles.label}>{item.label}</span>
                  <blockquote className={styles.quote}>«{item.quote}»</blockquote>
                </li>
              ))}
            </ul>
          ) : null}

          {/* Предварительная сумма вознаграждения — с сервера, от той же
              стартовой цены, что и начисление (§42.4). Окончательный расчёт —
              с основаниями и цитатами — появится ниже сразу после отметки. */}
          {estimate === null ? null : (
            <p className={styles.note}>
              {t('handover.outcome.feeEstimate', {
                fee: money(estimate.amount_cents, estimate.currency, locale, 'auto'),
              })}
            </p>
          )}
          <p className={styles.note}>{t('handover.outcome.feeFollows')}</p>
          <p className={styles.note}>{t('handover.outcome.fixItFirst')}</p>

          <div className={styles.actions}>
            <Button
              disabled={pending}
              onClick={() => {
                onRecord('purchased', true);
              }}
            >
              {pending ? t('handover.outcome.pending') : t('handover.outcome.confirm')}
            </Button>
            <Button
              variant="ghost"
              disabled={pending}
              onClick={() => {
                setChoice(null);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}

      <p className={styles.note}>{t('handover.outcome.notForAlgorithms')}</p>
    </section>
  );
}

function errorText(error: unknown, t: (key: string) => string): string | null {
  if (!(error instanceof ApiError)) return null;
  const key = `handover.error.code.${error.code}`;
  const translated = t(key);
  return translated === key ? t('handover.error.text') : translated;
}
